import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";

export const NATIVE_INSETS_MESSAGE_TYPE = "NATIVE_INSETS";
const STORAGE_KEY = "smsp_native_insets";

/** Flutter dart:io Platform.operatingSystem values this app actually sends. */
export type NativeAppOS = "ios" | "android";

export function parseNativeAppOS(value: unknown): NativeAppOS | undefined {
    return value === "ios" || value === "android" ? value : undefined;
}

export type NativeWebViewInsets = {
    paddingTop: number;
    paddingBottom: number;
    paddingLeft: number;
    paddingRight: number;
    appOS?: NativeAppOS;
    isNativeWebView: boolean;
};

export type NativeWebViewInsetsPadding = Pick<
    NativeWebViewInsets,
    "paddingTop" | "paddingBottom" | "paddingLeft" | "paddingRight"
>;

type StoredNativeInsets = NativeWebViewInsetsPadding & {
    appOS?: NativeAppOS;
};

type ContentPaddingOptions = {
    top?: boolean;
    bottom?: boolean;
    left?: boolean;
    right?: boolean;
};

const DEFAULT_INSETS: NativeWebViewInsets = {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    isNativeWebView: false,
};

const subscribers = new Set<(insets: NativeWebViewInsets) => void>();
let messageListenerRegistered = false;

function parsePaddingValue(value: string | null | undefined): number | null {
    if (value == null || value === "") return null;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function hasInsetParams(params: URLSearchParams): boolean {
    return (
        parsePaddingValue(params.get("paddingTop")) != null ||
        parsePaddingValue(params.get("paddingBottom")) != null ||
        !!params.get("appOS")
    );
}

function readStoredInsets(): StoredNativeInsets | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<StoredNativeInsets>;
        return {
            paddingTop: parsePaddingValue(String(parsed.paddingTop ?? "")) ?? 0,
            paddingBottom: parsePaddingValue(String(parsed.paddingBottom ?? "")) ?? 0,
            paddingLeft: parsePaddingValue(String(parsed.paddingLeft ?? "")) ?? 0,
            paddingRight: parsePaddingValue(String(parsed.paddingRight ?? "")) ?? 0,
            appOS: parseNativeAppOS(parsed.appOS),
        };
    } catch {
        return null;
    }
}

function writeStoredInsets(insets: StoredNativeInsets): void {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(insets));
    } catch {
        // ignore unavailable storage
    }
}

function readLocalAppOS(): NativeAppOS | undefined {
    if (typeof window === "undefined") return undefined;
    try {
        return parseNativeAppOS(window.localStorage.getItem("appOS"));
    } catch {
        return undefined;
    }
}

function syncAppOSToLocalStorage(appOS?: NativeAppOS): void {
    if (!appOS || typeof window === "undefined") return;
    try {
        window.localStorage.setItem("appOS", appOS);
    } catch {
        // ignore unavailable storage
    }
}

function computeIsNativeWebView(
    padding: NativeWebViewInsetsPadding,
    appOS?: NativeAppOS,
    urlHasInsetParams = false
): boolean {
    if (urlHasInsetParams) return true;
    if (appOS) return true;
    if (readLocalAppOS()) return true;
    return (
        padding.paddingTop > 0 ||
        padding.paddingBottom > 0 ||
        padding.paddingLeft > 0 ||
        padding.paddingRight > 0
    );
}

function buildInsets(
    padding: NativeWebViewInsetsPadding,
    appOS?: NativeAppOS,
    urlHasInsetParams = false
): NativeWebViewInsets {
    const resolvedAppOS = appOS ?? readLocalAppOS();
    return {
        ...padding,
        appOS: resolvedAppOS,
        isNativeWebView: computeIsNativeWebView(padding, resolvedAppOS, urlHasInsetParams),
    };
}

function notifySubscribers(insets: NativeWebViewInsets): void {
    subscribers.forEach((listener) => listener(insets));
}

export function parseNativeWebViewInsetsFromSearch(search: string): NativeWebViewInsets {
    const params = new URLSearchParams(search);
    const urlHasInsetParams = hasInsetParams(params);
    const padding: NativeWebViewInsetsPadding = {
        paddingTop: parsePaddingValue(params.get("paddingTop")) ?? 0,
        paddingBottom: parsePaddingValue(params.get("paddingBottom")) ?? 0,
        paddingLeft: 0,
        paddingRight: 0,
    };
    const appOS = parseNativeAppOS(params.get("appOS"));
    return buildInsets(padding, appOS, urlHasInsetParams);
}

export function parseNativeInsetsMessage(data: unknown): Partial<NativeWebViewInsetsPadding> | null {
    let message: unknown = data;
    if (typeof data === "string") {
        try {
            message = JSON.parse(data);
        } catch {
            return null;
        }
    }

    if (!message || typeof message !== "object") return null;
    const payload = message as Record<string, unknown>;
    if (payload.type !== NATIVE_INSETS_MESSAGE_TYPE) return null;

    const partial: Partial<NativeWebViewInsetsPadding> = {};
    const top = parsePaddingValue(
        payload.paddingTop == null ? null : String(payload.paddingTop)
    );
    const bottom = parsePaddingValue(
        payload.paddingBottom == null ? null : String(payload.paddingBottom)
    );
    const left = parsePaddingValue(
        payload.paddingLeft == null ? null : String(payload.paddingLeft)
    );
    const right = parsePaddingValue(
        payload.paddingRight == null ? null : String(payload.paddingRight)
    );

    if (top != null) partial.paddingTop = top;
    if (bottom != null) partial.paddingBottom = bottom;
    if (left != null) partial.paddingLeft = left;
    if (right != null) partial.paddingRight = right;

    return Object.keys(partial).length > 0 ? partial : null;
}

export function getNativeSafeAreaCSSVars(
    insets: NativeWebViewInsetsPadding
): Record<string, string> {
    return {
        "--native-padding-top": `${insets.paddingTop}px`,
        "--native-padding-bottom": `${insets.paddingBottom}px`,
        "--native-padding-left": `${insets.paddingLeft}px`,
        "--native-padding-right": `${insets.paddingRight}px`,
    };
}

export function applyNativeSafeAreaCSSVars(insets: NativeWebViewInsetsPadding): void {
    if (typeof document === "undefined") return;
    Object.assign(document.documentElement.style, getNativeSafeAreaCSSVars(insets));
}

export function getContentPaddingStyle(
    insets: NativeWebViewInsetsPadding,
    options: ContentPaddingOptions = { top: true, bottom: true, left: true, right: true }
): Record<string, string> {
    const style: Record<string, string> = {};
    if (options.top !== false && insets.paddingTop > 0) {
        style.paddingTop = `${insets.paddingTop}px`;
    }
    if (options.bottom !== false && insets.paddingBottom > 0) {
        style.paddingBottom = `${insets.paddingBottom}px`;
    }
    if (options.left !== false && insets.paddingLeft > 0) {
        style.paddingLeft = `${insets.paddingLeft}px`;
    }
    if (options.right !== false && insets.paddingRight > 0) {
        style.paddingRight = `${insets.paddingRight}px`;
    }
    return style;
}

export function nativeInsetSx(
    insets: NativeWebViewInsets,
    edge: "top" | "bottom" | "left" | "right" | "both" | "horizontal" | "vertical"
): SystemStyleObject<Theme> {
    if (!insets.isNativeWebView) return {};

    switch (edge) {
        case "top":
            return insets.paddingTop > 0 ? { paddingTop: `${insets.paddingTop}px` } : {};
        case "bottom":
            return insets.paddingBottom > 0 ? { paddingBottom: `${insets.paddingBottom}px` } : {};
        case "left":
            return insets.paddingLeft > 0 ? { paddingLeft: `${insets.paddingLeft}px` } : {};
        case "right":
            return insets.paddingRight > 0 ? { paddingRight: `${insets.paddingRight}px` } : {};
        case "horizontal":
            return getContentPaddingStyle(insets, { top: false, bottom: false });
        case "vertical":
            return getContentPaddingStyle(insets, { left: false, right: false });
        case "both":
        default:
            return getContentPaddingStyle(insets);
    }
}

export function loadNativeWebViewInsets(): NativeWebViewInsets {
    const stored = readStoredInsets();
    if (stored) {
        return buildInsets(stored, stored.appOS);
    }
    const appOS = readLocalAppOS();
    return buildInsets(
        {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
        },
        appOS
    );
}

export function updateNativeWebViewInsets(
    partial: Partial<NativeWebViewInsetsPadding> & { appOS?: NativeAppOS }
): NativeWebViewInsets {
    const current = loadNativeWebViewInsets();
    const nextPadding: NativeWebViewInsetsPadding = {
        paddingTop: partial.paddingTop ?? current.paddingTop,
        paddingBottom: partial.paddingBottom ?? current.paddingBottom,
        paddingLeft: partial.paddingLeft ?? current.paddingLeft,
        paddingRight: partial.paddingRight ?? current.paddingRight,
    };
    const appOS = partial.appOS ?? current.appOS;
    const next = buildInsets(nextPadding, appOS, current.isNativeWebView);

    writeStoredInsets({
        ...nextPadding,
        appOS,
    });
    syncAppOSToLocalStorage(appOS);
    applyNativeSafeAreaCSSVars(nextPadding);
    notifySubscribers(next);
    return next;
}

function handleNativeInsetsMessage(event: MessageEvent): void {
    const partial = parseNativeInsetsMessage(event.data);
    if (!partial) return;
    updateNativeWebViewInsets(partial);
}

export function subscribeNativeInsetsUpdates(
    listener: (insets: NativeWebViewInsets) => void
): () => void {
    subscribers.add(listener);
    return () => {
        subscribers.delete(listener);
    };
}

export function registerNativeInsetsMessageListener(): void {
    if (typeof window === "undefined" || messageListenerRegistered) return;
    window.addEventListener("message", handleNativeInsetsMessage);
    messageListenerRegistered = true;
}

export function bootstrapNativeWebViewInsets(): NativeWebViewInsets {
    if (typeof window === "undefined") {
        return { ...DEFAULT_INSETS };
    }

    const search = window.location.search;
    let insets: NativeWebViewInsets;

    if (hasInsetParams(new URLSearchParams(search))) {
        insets = parseNativeWebViewInsetsFromSearch(search);
        writeStoredInsets({
            paddingTop: insets.paddingTop,
            paddingBottom: insets.paddingBottom,
            paddingLeft: insets.paddingLeft,
            paddingRight: insets.paddingRight,
            appOS: insets.appOS,
        });
        syncAppOSToLocalStorage(insets.appOS);
    } else {
        insets = loadNativeWebViewInsets();
    }

    applyNativeSafeAreaCSSVars(insets);
    registerNativeInsetsMessageListener();

    if (process.env.NODE_ENV === "development") {
        (window as Window & { __nativeWebViewInsets?: NativeWebViewInsets }).__nativeWebViewInsets =
            insets;
    }

    return insets;
}
